/**
 * Meta WhatsApp Embedded Signup — Coexistence launcher.
 *
 * Drives Meta's in-browser "Embedded Signup" so an operator can connect a
 * sales agent's existing WhatsApp Business App number to our platform without
 * the rep losing the app on their phone (that's the "Coexistence" feature
 * type). The flow yields two values that the backend needs:
 *
 *   - `code`     — an OAuth authorization code from the FB.login callback,
 *                  which the backend exchanges for a system-user access token.
 *   - `wabaId`   — the WhatsApp Business Account id, delivered out-of-band via
 *                  a `window` `message` event (postMessage) from the popup.
 *
 * Both arrive asynchronously and independently, so we collect them and only
 * resolve once we have the pair. See:
 *   https://developers.facebook.com/docs/whatsapp/embedded-signup
 */

const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const SDK_LOAD_TIMEOUT_MS = 15000;
const SIGNUP_TIMEOUT_MS = 5 * 60 * 1000; // 5 min — user may need to log in + pick a number.

// Minimal, contained typing for the Facebook JS SDK global. We only touch the
// two methods we use; `any` keeps this from pulling in the full FB type surface.
declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

/** Build-time Meta config. Empty when the env vars aren't set for this build. */
export function metaConfig(): { appId: string; configId: string } {
  return {
    appId: import.meta.env.VITE_META_APP_ID ?? "",
    configId: import.meta.env.VITE_META_ES_CONFIG_ID ?? ""
  };
}

/** True only when both the app id and the Embedded Signup config id are present. */
export function isMetaSignupConfigured(): boolean {
  const { appId, configId } = metaConfig();
  return Boolean(appId && configId);
}

let sdkPromise: Promise<void> | null = null;

/**
 * Lazy-load the Facebook SDK exactly once and initialise it. Subsequent calls
 * return the same in-flight (or settled) promise so the script tag and
 * FB.init() never run twice.
 */
function loadFacebookSdk(): Promise<void> {
  if (sdkPromise) return sdkPromise;

  const { appId } = metaConfig();
  if (!appId) {
    return Promise.reject(new Error("WhatsApp is not configured (missing VITE_META_APP_ID)."));
  }

  sdkPromise = new Promise<void>((resolve, reject) => {
    const init = () => {
      try {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          version: "v21.0"
        });
        resolve();
      } catch (err) {
        reject(err instanceof Error ? err : new Error("Failed to initialise the Facebook SDK."));
      }
    };

    // SDK already on the page from an earlier mount.
    if (window.FB) {
      init();
      return;
    }

    const timeout = window.setTimeout(() => {
      sdkPromise = null; // allow a retry on the next attempt
      reject(new Error("Timed out loading the Facebook SDK. Check your connection and try again."));
    }, SDK_LOAD_TIMEOUT_MS);

    // The SDK calls fbAsyncInit once it has parsed.
    window.fbAsyncInit = () => {
      window.clearTimeout(timeout);
      init();
    };

    const existing = document.getElementById("facebook-jssdk") as HTMLScriptElement | null;
    if (existing) {
      // Script tag present but FB not ready yet — fbAsyncInit above will fire.
      return;
    }

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = FB_SDK_SRC;
    script.onerror = () => {
      window.clearTimeout(timeout);
      sdkPromise = null; // allow a retry
      reject(new Error("Could not load the Facebook SDK script."));
    };
    document.body.appendChild(script);
  });

  return sdkPromise;
}

/**
 * Launch Meta's Embedded Signup in Coexistence mode and resolve with the
 * `{ code, wabaId }` pair once both are available. The promise rejects if the
 * user cancels, the SDK fails to load, or the flow times out.
 */
export async function launchCoexistenceSignup(): Promise<{ code: string; wabaId: string }> {
  const { configId } = metaConfig();
  if (!configId) {
    throw new Error("WhatsApp is not configured (missing VITE_META_ES_CONFIG_ID).");
  }

  await loadFacebookSdk();

  return new Promise<{ code: string; wabaId: string }>((resolve, reject) => {
    let code: string | null = null;
    let wabaId: string | null = null;
    let settled = false;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeout);
    };

    const finish = () => {
      if (settled) return;
      if (code && wabaId) {
        settled = true;
        cleanup();
        resolve({ code, wabaId });
      }
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    // The WABA id arrives via postMessage, separately from the login callback.
    const onMessage = (event: MessageEvent) => {
      // Only trust messages from Facebook's own origin.
      if (typeof event.origin !== "string" || !event.origin.endsWith("facebook.com")) return;
      if (typeof event.data !== "string") return;
      try {
        const parsed = JSON.parse(event.data);
        if (
          parsed &&
          parsed.type === "WA_EMBEDDED_SIGNUP" &&
          parsed.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" &&
          parsed.data &&
          parsed.data.waba_id
        ) {
          wabaId = String(parsed.data.waba_id);
          finish();
        }
      } catch {
        // Non-JSON or unrelated message — ignore.
      }
    };

    window.addEventListener("message", onMessage);

    const timeout = window.setTimeout(() => {
      fail("WhatsApp connection timed out. Please try again.");
    }, SIGNUP_TIMEOUT_MS);

    try {
      window.FB.login(
        (response: any) => {
          const authCode = response?.authResponse?.code;
          if (authCode) {
            code = String(authCode);
            finish();
          } else {
            // User closed the dialog or denied — no code means we can't continue.
            fail("WhatsApp connection was cancelled.");
          }
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
            sessionInfoVersion: "3"
          }
        }
      );
    } catch (err) {
      fail(err instanceof Error ? err.message : "Could not open the WhatsApp signup dialog.");
    }
  });
}
